import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export interface ConfirmOptions {
  title: string;
  description?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  destructive?: boolean;
}

export interface PromptOptions {
  title: string;
  description?: ReactNode;
  label?: string;
  placeholder?: string;
  defaultValue?: string;
  confirmText?: string;
  cancelText?: string;
  required?: boolean;
}

interface DialogImperativeCtx {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  prompt: (opts: PromptOptions) => Promise<string | null>;
}

const Ctx = createContext<DialogImperativeCtx | null>(null);

export function useConfirm(): DialogImperativeCtx["confirm"] {
  const c = useContext(Ctx);
  if (!c) throw new Error("useConfirm must be used inside <DialogImperativeProvider>");
  return c.confirm;
}

export function usePrompt(): DialogImperativeCtx["prompt"] {
  const c = useContext(Ctx);
  if (!c) throw new Error("usePrompt must be used inside <DialogImperativeProvider>");
  return c.prompt;
}

type ConfirmState =
  | (ConfirmOptions & { resolve: (v: boolean) => void })
  | null;
type PromptState =
  | (PromptOptions & { resolve: (v: string | null) => void })
  | null;

export function DialogImperativeProvider({
  children,
}: {
  children: ReactNode;
}): ReactNode {
  const [confirmState, setConfirmState] = useState<ConfirmState>(null);
  const [promptState, setPromptState] = useState<PromptState>(null);
  const [promptValue, setPromptValue] = useState("");

  const confirm = useCallback(
    (opts: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        setConfirmState({ ...opts, resolve });
      }),
    [],
  );

  const prompt = useCallback(
    (opts: PromptOptions) =>
      new Promise<string | null>((resolve) => {
        setPromptValue(opts.defaultValue ?? "");
        setPromptState({ ...opts, resolve });
      }),
    [],
  );

  // Clear state BEFORE invoking the resolver — if the consumer
  // immediately opens another dialog inside its .then(), we don't
  // want our own setState to wipe the new state.
  const resolveConfirm = (v: boolean): void => {
    const s = confirmState;
    setConfirmState(null);
    s?.resolve(v);
  };
  const resolvePrompt = (v: string | null): void => {
    const s = promptState;
    setPromptState(null);
    s?.resolve(v);
  };

  const promptRequired = promptState?.required !== false;
  const promptCanSubmit = !promptRequired || promptValue.trim().length > 0;

  return (
    <Ctx.Provider value={{ confirm, prompt }}>
      {children}

      <AlertDialog
        open={confirmState != null}
        onOpenChange={(open) => {
          if (!open) resolveConfirm(false);
        }}
      >
        {confirmState && (
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{confirmState.title}</AlertDialogTitle>
              {confirmState.description != null && (
                <AlertDialogDescription>
                  {confirmState.description}
                </AlertDialogDescription>
              )}
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => resolveConfirm(false)}>
                {confirmState.cancelText ?? "Cancel"}
              </AlertDialogCancel>
              <AlertDialogAction
                variant={confirmState.destructive ? "destructive" : "default"}
                onClick={() => resolveConfirm(true)}
              >
                {confirmState.confirmText ?? "Confirm"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        )}
      </AlertDialog>

      <Dialog
        open={promptState != null}
        onOpenChange={(open) => {
          if (!open) resolvePrompt(null);
        }}
      >
        {promptState && (
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{promptState.title}</DialogTitle>
              {promptState.description != null && (
                <DialogDescription>{promptState.description}</DialogDescription>
              )}
            </DialogHeader>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (promptCanSubmit) resolvePrompt(promptValue.trim());
              }}
              className="flex flex-col gap-2"
            >
              {promptState.label && (
                <Label htmlFor="dialog-imperative-prompt-input">
                  {promptState.label}
                </Label>
              )}
              <Input
                id="dialog-imperative-prompt-input"
                autoFocus
                value={promptValue}
                placeholder={promptState.placeholder}
                onChange={(e) => setPromptValue(e.target.value)}
              />
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => resolvePrompt(null)}
                >
                  {promptState.cancelText ?? "Cancel"}
                </Button>
                <Button type="submit" disabled={!promptCanSubmit}>
                  {promptState.confirmText ?? "Confirm"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        )}
      </Dialog>
    </Ctx.Provider>
  );
}
